package com.essntl.features.message.ui.chat

import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.core.utils.viewmodel.ViewModel
import com.essntl.core.utils.viewmodel.collectSuccessEvent
import com.essntl.core.utils.viewmodel.getData
import com.essntl.features.client.domain.repository.ClientRepository
import com.essntl.features.message.domain.model.MessageModel
import com.essntl.features.message.domain.model.WhapiMessageAction
import com.essntl.features.message.domain.model.WhapiMessageModel
import com.essntl.features.message.domain.repository.ConversationRepository
import com.essntl.features.message.domain.repository.MessageRepository
import com.essntl.features.notification.domain.repository.NotificationRepository
import com.essntl.features.profile.domain.model.ProfileModel
import com.essntl.features.profile.domain.use_case.GetCurrentUserUseCase
import com.essntl.features.supplier.domain.repository.SupplierRepository
import com.essntl.features.travelagent.domain.repository.TravelAgentRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class ChatViewModel(
    private val messageRepository: MessageRepository,
    private val notificationRepository: NotificationRepository,
    private val conversationRepository: ConversationRepository,
    private val clientRepository: ClientRepository,
    private val supplierRepository: SupplierRepository,
    private val travelAgentRepository: TravelAgentRepository,
    override val scope: CoroutineScope,
    override val loadingManager: LoadingManager,
    private val state: MutableStateFlow<ChatState>,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
) : ViewModel {

    init {
        getCurrentProfile()

        collectSuccessEvent(
            flow = getCurrentUserUseCase.successEvent,
            getState = { state.value.getCurrentProfile },
            setState = { newState -> state.update { it.copy(getCurrentProfile = newState) } },
        )
    }

    fun onEvent(event: ChatEvent) {
        when (event) {
            is ChatEvent.GetAllWhapiMessagesByChatId -> {
                getAllWhapiMessagesByChatId(event)
                getAllWhapiCallsByChatId(event)
            }

            is ChatEvent.SendWhatsAppMessage -> sendWhatsAppMessage(event)
            is ChatEvent.GetWhapiChatById -> getWhapiChatById(event)
            is ChatEvent.GetWhapiGroupById -> getWhapiGroupById(event)
            is ChatEvent.ReactToMessage -> reactToMessage(event)
            is ChatEvent.RemoveParticipantFromGroup -> removeParticipants(event)
        }
    }

    private fun getAllWhapiMessagesByChatId(event: ChatEvent.GetAllWhapiMessagesByChatId) =
        getData(
            getData = { messageRepository.getWhapiChatMessages(event.chatId) },
            getState = { state.value.getAllWhapiMessages },
            setState = { newState -> state.update { it.copy(getAllWhapiMessages = newState) } },
            onSuccess = { messages ->
                val whapiIds = messages.map { it.id }
                getMessages(whapiIds = whapiIds)
            },
        )

    private fun getAllWhapiCallsByChatId(event: ChatEvent.GetAllWhapiMessagesByChatId) =
        getData(
            getData = { notificationRepository.getWhapiCallsNotificationsByChatId(event.chatId) },
            getState = { state.value.getAllWhapiCalls },
            setState = { newState -> state.update { it.copy(getAllWhapiCalls = newState) } },
            onSuccess = { messages ->
                val whapiIds = messages.map { it.id }
                getMessages(whapiIds = whapiIds)
            },
        )

    private fun sendWhatsAppMessage(event: ChatEvent.SendWhatsAppMessage) =
        scope.launch {
            messageRepository.sendWhapiTextMessages(
                text = event.text,
                to = event.chatId,
            ).onSuccess { newMessage ->
                event.onSuccess(newMessage)

                // Insert Message to DB
                insertMessage(
                    MessageModel.empty.copy(
                        type = MessageModel.Type.Whatsapp(event.chatId),
                        userText = event.text,
                        platformId = newMessage.id,
                        sender = state.value.getCurrentProfile.data ?: ProfileModel.empty,
                    ),
                )

                val updatedMessagesList = listOf(newMessage) + state.value.getAllWhapiMessages.data.orEmpty()
                state.update { state ->
                    state.copy(
                        getAllWhapiMessages = state.getAllWhapiMessages.copy(
                            data = updatedMessagesList,
                        ),
                    )
                }
            }
        }

    private fun getWhapiChatById(event: ChatEvent.GetWhapiChatById) =
        getData(
            getData = { conversationRepository.getWhapiChatById(event.conversationId) },
            getState = { state.value.getWhapiChat },
            setState = { newState -> state.update { it.copy(getWhapiChat = newState) } },
            onSuccess = { chat ->
                getChatClient(setOf(chat.phoneNumber))
            },
        )

    private fun getWhapiGroupById(event: ChatEvent.GetWhapiGroupById) =
        getData(
            getData = { conversationRepository.getWhapiGroupById(event.conversationId) },
            getState = { state.value.getWhapiChat },
            setState = { newState -> state.update { it.copy(getWhapiChat = newState) } },
            onSuccess = { group ->
                val phoneNumbers = group.participants.orEmpty().map { it.id }
                getChatClient(phoneNumbers = phoneNumbers.toSet())
                getSupplierByPhoneNumber(phoneNumbers = phoneNumbers.toSet())
                getTravelAgentByPhoneNumber(phoneNumbers = phoneNumbers.toSet())
            },
        )

    private fun reactToMessage(event: ChatEvent.ReactToMessage) =
        scope.launch {
            messageRepository.reactToMessage(
                messageId = event.whapiMessageId,
                emoji = event.emoji ?: "",
            ).onSuccess { _ ->
                val message = WhapiMessageModel.empty.copy(
                    fromMe = true,
                    type = WhapiMessageModel.Type.Action,
                    action = WhapiMessageAction.empty.copy(
                        emoji = event.emoji,
                        target = event.whapiMessageId,
                    ),
                )
                event.onSuccess(message)

                // Remove locally last Reaction
                val filteredMessages = state.value.getAllWhapiMessages.data.orEmpty().toMutableList()
                filteredMessages.removeAll {
                    it.type == WhapiMessageModel.Type.Action &&
                        it.action?.target == event.whapiMessageId &&
                        it.fromMe
                }

                val updatedMessagesList = listOf(message) + filteredMessages
                state.update { state ->
                    state.copy(
                        getAllWhapiMessages = state.getAllWhapiMessages.copy(
                            data = updatedMessagesList,
                        ),
                    )
                }
            }
        }

    private fun removeParticipants(event: ChatEvent.RemoveParticipantFromGroup) {
        scope.launch {
            messageRepository.removeContactFromGroup(
                groupId = event.groupId,
                participants = setOf(event.participants),
            ).onSuccess {
                val updatedClientsList =
                    state.value.getClients.data.orEmpty().filter { it.telephone != event.participants }
                val updatedSuppliersList =
                    state.value.getAllSupplier.data.orEmpty().filter { it.contact?.phoneNumber != event.participants }
                val updatedTAList =
                    state.value.getAllTravelAgent.data.orEmpty()
                        .filter { it.contact?.phoneNumber != event.participants }

                state.update { state ->
                    state.copy(
                        getClients = state.getClients.copy(data = updatedClientsList),
                        getAllSupplier = state.getAllSupplier.copy(data = updatedSuppliersList),
                        getAllTravelAgent = state.getAllTravelAgent.copy(data = updatedTAList),
                    )
                }
            }
        }
    }

    private fun getCurrentProfile() =
        scope.launch {
            getCurrentUserUseCase.get()
        }

    private fun getMessages(whapiIds: List<String>) =
        getData(
            getData = { messageRepository.getMessagesByWhapiIds(whapiIds = whapiIds.toSet()) },
            getState = { state.value.getAllMessages },
            setState = { newState -> state.value = state.value.copy(getAllMessages = newState) },
        )

    private fun insertMessage(message: MessageModel) =
        getData(
            getData = { messageRepository.insertMessage(message = message) },
            setState = { },
            onSuccess = { newMessage ->
                val updatedMessagesList = state.value.getAllMessages.data.orEmpty() + newMessage
                state.update { state ->
                    state.copy(getAllMessages = state.getAllMessages.copy(data = updatedMessagesList))
                }
            },
        )

    private fun getChatClient(phoneNumbers: Set<String>) {
        getData(
            getData = { clientRepository.getByPhoneNumbers(phoneNumbers) },
            getState = { state.value.getClients },
            setState = { newState -> state.update { it.copy(getClients = newState) } },
        )
    }

    private fun getSupplierByPhoneNumber(phoneNumbers: Set<String>) =
        getData(
            getData = { supplierRepository.getByPhoneNumbers(phoneNumbers) },
            getState = { state.value.getAllSupplier },
            setState = { newState -> state.update { it.copy(getAllSupplier = newState) } },
        )

    private fun getTravelAgentByPhoneNumber(phoneNumbers: Set<String>) =
        getData(
            getData = { travelAgentRepository.getByPhoneNumbers(phoneNumbers) },
            getState = { state.value.getAllTravelAgent },
            setState = { newState -> state.update { it.copy(getAllTravelAgent = newState) } },
        )

}